import React from 'react';
import { TiArrowSortedUp, TiArrowSortedDown } from 'react-icons/ti';
import '../../../../styles/ScheduleTable.css';
import '../../../../styles/CommercialLiveSkeleton.css';

interface ILeagueLoading {
    leagueName: string;
    isFirstHalf?: boolean;
}

interface ICommercialLiveSkeletonProps {
    rows?: number;
    leagues?: ILeagueLoading[];
}

const formattedToday = new Date()
    .toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    })
    .toUpperCase();

function SkeletonOddBox() {
    return <div className="sk-odd-box" />;
}

function SkeletonMarketSlot({ index }: { index: number }) {
    const visibilityClass = index === 0 || index === 2 ? '' : 'd-none';

    return (
        <div
            className={`market-${index} ${visibilityClass} d-lg-flex market-center main-markets`}
        >
            <SkeletonOddBox />
        </div>
    );
}

function SkeletonLine() {
    return (
        <div className="line-container">
            <div className="team-name-container d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    <span className="sport-logos sk-avatar sk-shimmer" />
                    <span className="sk-team-bar sk-shimmer" />
                </div>
            </div>

            <SkeletonMarketSlot index={0} />
            <SkeletonMarketSlot index={1} />
            <SkeletonMarketSlot index={2} />
            <SkeletonMarketSlot index={3} />
        </div>
    );
}

function SkeletonGame() {
    return (
        <div className="gamesGroup">
            <div className="container-time">
                <span className="group-text-live group-text-live--period teko sk-live-pill">(1st)</span>
            </div>

            <div className="pru">
                <SkeletonLine />
                <SkeletonLine />
            </div>
        </div>
    );
}

function LiveLeagueTableHeader({ leagueName, isFirstHalf }: { leagueName: string; isFirstHalf?: boolean }) {
    return (
        <>
            <div className="schedule-table">
                <div className="container-table">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="market-title teko">
                            {leagueName} Live
                        </span>
                        {isFirstHalf && (
                            <div className="market-title teko">1st half</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="schedule-sticky-columns schedule-sticky-columns--live">
                <div className="d-flex align-items-center color-title-live">
                    <div className="container-date team-name-container">
                        <span className="group ps-1">{formattedToday}</span>
                    </div>

                    <div className="spread-container">
                        <span className="title-with-arrows">
                            <span className="title-table-game d-none d-lg-block">SPREAD</span>
                            <span className="title-table-game d-lg-none">SPREAD / ML</span>
                            <span className="arrow-icons">
                                <TiArrowSortedUp className="arrow-up" />
                                <TiArrowSortedDown className="arrow-down" />
                            </span>
                        </span>
                    </div>

                    <div className="moneyline-container d-none d-lg-block">
                        <span className="title-table-game d-none d-lg-block">MONEYLINE</span>
                    </div>

                    <div className="total-container">
                        <span className="title-with-arrows">
                            <span className="title-table-game d-none d-lg-block">TOTAL</span>
                            <span className="title-table-game d-lg-none">TOTAL/TT</span>
                            <span className="arrow-icons">
                                <TiArrowSortedUp className="arrow-up" />
                                <TiArrowSortedDown className="arrow-down" />
                            </span>
                        </span>
                    </div>

                    <div className="over-under-container d-none d-lg-block">
                        <span className="title-table-game">TEAM TOTAL</span>
                    </div>
                </div>
            </div>
        </>
    );
}

function SkeletonGroup({ leagueName, isFirstHalf, rows }: { leagueName: string; isFirstHalf?: boolean; rows: number }) {
    return (
        <div className="containertable">
            <div className="league-group">
                <LiveLeagueTableHeader leagueName={leagueName} isFirstHalf={isFirstHalf} />

                {Array.from({ length: rows }).map((_, i) => (
                    <SkeletonGame key={i} />
                ))}
            </div>
        </div>
    );
}

export function CommercialLiveEmptyLeague({
    leagueName,
    isFirstHalf,
}: {
    leagueName: string;
    isFirstHalf?: boolean;
}) {
    return (
        <div className="containertable">
            <div className="league-group">
                <LiveLeagueTableHeader leagueName={leagueName} isFirstHalf={isFirstHalf} />

                <div className="gamesGroup live-empty-game" role="status">
                    <div className="container-time">
                        <span className="group-text-live group-text-live--period teko">Live</span>
                    </div>
                    <div className="live-empty-copy">
                        <span>
                            Nothing available yet.
                            <br />
                            Stay Tuned
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CommercialLiveSkeleton({
    rows = 3,
    leagues,
}: ICommercialLiveSkeletonProps) {
    const groups: ILeagueLoading[] =
        leagues && leagues.length > 0 ? leagues : [{ leagueName: "", isFirstHalf: false }];

    return (
        <div
            className="sk-wrapper"
            role="status"
            aria-live="polite"
            aria-label="Loading live betting odds"
        >
            {groups.map((g, i) => (
                <SkeletonGroup
                    key={`${g.leagueName}-${g.isFirstHalf ? "1stHalf" : "Live"}-${i}`}
                    leagueName={g.leagueName}
                    isFirstHalf={g.isFirstHalf}
                    rows={rows}
                />
            ))}
        </div>
    );
}
